import { Routes } from '@angular/router';
import {
  basicPersonalInformationGuard,
  userGuard
} from '@components/auth/auth.guard';
import { redirectProdigyToReportRoutes } from './prodigy/prodigy.guard';
import { redirectReportToDnaReportTabs } from './report/report.guard';

export const routes: Routes = [
  {
    path: 'activity',
    /**
     * Load activity routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./activity/activity.routes').then((m) => m.routes)
  },
  {
    path: 'article',
    /**
     * Load article routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./article/article.routes').then((m) => m.routes)
  },
  {
    path: 'asa-x',
    canActivate: [userGuard, basicPersonalInformationGuard],
    /**
     * Load asa-x module.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./asa-x/asa-x.routes').then((m) => m.routes)
  },
  {
    path: 'auth',
    /**
     * Load auth routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./auth/auth.routes').then((m) => m.authRoutes)
  },
  {
    path: 'consultation',
    canActivate: [userGuard, basicPersonalInformationGuard],
    /**
     * Load consultation routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./consultation/consultation.routes').then((m) => m.routes)
  },
  {
    path: 'daily',
    /**
     * Load daily routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./daily/daily.routes').then((item) => item.routes)
  },
  {
    path: 'delivery',
    canActivate: [userGuard],
    /**
     * Delivery routes.
     *
     * @returns {Routes} Delivery-routes.
     */
    loadChildren: () =>
      import('./delivery/delivery.routes').then((m) => m.routes)
  },
  {
    path: 'kit',
    canActivate: [basicPersonalInformationGuard],
    /**
     * Load kit routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./kit/kit.routes').then((m) => m.routes)
  },
  {
    path: 'lab',
    canActivate: [userGuard],
    /**
     * Lab routes.
     *
     * @returns {Routes} Lab-routes.
     */
    loadChildren: () => import('./lab/lab.routes').then((m) => m.routes)
  },
  {
    path: 'lwb',
    /**
     * Load LWB module.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./lwb/lwb.routes').then((m) => m.routes)
  },
  {
    path: 'media-library',
    /**
     * Load media-library routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./media-library/media-library.routes').then((m) => m.routes)
  },
  {
    path: 'order',
    /**
     * Order routes.
     *
     * @returns {Routes} Order-routes.
     */
    loadChildren: () => import('./order/order.routes').then((m) => m.routes)
  },
  {
    path: 'privacy-policy',
    /**
     * Load privacy policy module.
     *
     * @returns {Promise<typeof import('./privacy-policy/privacy-policy.page').PrivacyPolicyPage>}
     */
    loadComponent: () =>
      import('./privacy-policy/privacy-policy.page').then(
        (m) => m.PrivacyPolicyPage
      )
  },
  {
    // This route will no longer used and moved to /report/prodigy path.
    // if there is a navigation target this path, redirectProdigyToReportRoutes guard will redirect navigation to /report/prodigy/....
    path: 'prodigy',
    canActivate: [redirectProdigyToReportRoutes],
    /**
     * Load prodigy module.
     *
     * @returns {Routes} Prodigy routes.
     */
    loadChildren: () => import('./prodigy/prodigy.routes').then((m) => m.routes)
  },
  {
    path: 'profile',
    /**
     * Load profile module.
     *
     * @returns {Promise<Routes>} Profile-routes.
     */
    loadChildren: () => import('./profile/profile.routes').then((v) => v.routes)
  },
  {
    path: 'report',
    canActivate: [redirectReportToDnaReportTabs],
    /**
     * Load reports module.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./report/report.routes').then((v) => v.routes)
  },
  {
    path: 'shop',
    /**
     * Load shop routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./shop/shop.routes').then((m) => m.routes)
  },
  {
    path: 'support',
    /**
     * Load support module.
     *
     * @returns {Promise<typeof import('./support/support.page').SupportPage>}
     */
    loadComponent: () =>
      import('./support/support.page').then((m) => m.SupportPage)
  },
  {
    path: 'terms-of-service',
    /**
     * Load TOS module.
     *
     * @returns {Promise<typeof import('./terms-of-service/terms-of-service.page').TermsOfServicePage>}
     */
    loadComponent: () =>
      import('./terms-of-service/terms-of-service.page').then(
        (m) => m.TermsOfServicePage
      )
  },
  {
    path: 'tracker',
    canActivate: [userGuard],
    /**
     * Load tracker module.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./tracker/tracker.routes').then((m) => m.routes)
  },
  {
    path: 'tabs',
    canActivate: [userGuard],
    /**
     * Load tabs module.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./tabs/tabs.routes').then((item) => item.routes)
  },
  {
    path: 'ehr',
    canActivate: [userGuard, basicPersonalInformationGuard],
    /**
     * Load ehr module.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./ehr/ehr.routes').then((v) => v.routes)
  },
  {
    path: 'greetings',
    canActivate: [userGuard],
    /**
     * Load greetings module.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./greetings/greetings.routes').then((v) => v.routes)
  },
  {
    path: 'health-assessment',
    /**
     * Load health assessment routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./health-assessment/health-assessment.routes').then(
        (m) => m.routes
      )
  },
  {
    path: 'health-passport',
    /**
     * Load health-passport routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./health-passport/health-passport.routes').then(
        (m) => m.healthPassportRoutes
      )
  },
  {
    path: 'questionnaire',
    /**
     * Load questionnaire routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./questionnaire/questionnaire.routes').then((item) => item.routes)
  },
  {
    path: 'assistant',
    /**
     * Load assistant routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./assistant/assistant.routes').then((item) => item.routes)
  },
  {
    path: 'avatar',
    canActivate: [userGuard],
    /**
     * Load avatar routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () => import('./avatar/avatar.routes').then((m) => m.routes)
  },
  {
    path: 'daily',
    /**
     * Load daily routes.
     *
     * @returns {Promise<Routes>}
     */
    loadChildren: () =>
      import('./daily/daily.routes').then((item) => item.routes)
  },
  {
    path: 'home',
    redirectTo: 'tabs/home',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: 'tabs/home'
  }
];
